import { __decorate, __metadata } from 'tslib';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Directive, NgModule } from '@angular/core';
import { CSSModifier, CSSComponent } from '@odx/angular/internal';
import { injectElement } from '@odx/angular/utils';
import { CoreModule } from '@odx/angular';
import { ActionGroupComponent } from '@odx/angular/components/action-group';
import { AvatarComponent } from '@odx/angular/components/avatar';
import { ButtonComponent } from '@odx/angular/components/button';
import { IconComponent } from '@odx/angular/components/icon';

const AreaHeaderSize = {
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    XLARGE: 'xlarge',
};

/**
 * Represents the AreaHeaderComponent.
 */
let AreaHeaderComponent = class AreaHeaderComponent {
    constructor() {
        this.element = injectElement();
        /**
         * The size of the area header.
         *
         * @type {AreaHeaderSize}
         * @default AreaHeaderSize.LARGE
         */
        this.size = AreaHeaderSize.LARGE;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: AreaHeaderComponent, isStandalone: true, selector: "odx-area-header", inputs: { size: "size" }, ngImport: i0, template: "<div class=\"odx-area-header__container\">\n  <ng-content select=\"button[odxNavigationBack], a[odxNavigationBack]\" />\n  <ng-content select=\"odx-avatar\" />\n  <div class=\"odx-area-header__inner\">\n    <div class=\"odx-area-header__title\">\n      <ng-content />\n    </div>\n    <ng-content select=\"odx-area-header-subtitle\" />\n  </div>\n</div>\n<ng-content select=\"odx-area-header-content\" />\n", dependencies: [{ kind: "ngmodule", type: CommonModule }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", String)
], AreaHeaderComponent.prototype, "size", void 0);
AreaHeaderComponent = __decorate([
    CSSComponent('area-header')
], AreaHeaderComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-area-header', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, imports: [CommonModule], standalone: true, template: "<div class=\"odx-area-header__container\">\n  <ng-content select=\"button[odxNavigationBack], a[odxNavigationBack]\" />\n  <ng-content select=\"odx-avatar\" />\n  <div class=\"odx-area-header__inner\">\n    <div class=\"odx-area-header__title\">\n      <ng-content />\n    </div>\n    <ng-content select=\"odx-area-header-subtitle\" />\n  </div>\n</div>\n<ng-content select=\"odx-area-header-content\" />\n" }]
        }], propDecorators: { size: [{
                type: Input
            }] } });

/**
 * Directive for the content of the area header.
 */
class AreaHeaderContentDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: AreaHeaderContentDirective, isStandalone: true, selector: "odx-area-header-content", host: { properties: { "class.odx-area-header__content": "true" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'odx-area-header-content',
                    standalone: true,
                    host: {
                        '[class.odx-area-header__content]': 'true',
                    },
                }]
        }] });

/**
 * Directive for displaying a subtitle in the area header component.
 */
class AreaHeaderSubtitleDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderSubtitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: AreaHeaderSubtitleDirective, isStandalone: true, selector: "odx-area-header-subtitle", host: { properties: { "class.odx-area-header__subtitle": "true" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderSubtitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'odx-area-header-subtitle',
                    standalone: true,
                    host: {
                        '[class.odx-area-header__subtitle]': 'true',
                    },
                }]
        }] });

const modules = [
    AreaHeaderComponent,
    AreaHeaderContentDirective,
    AreaHeaderSubtitleDirective,
    AvatarComponent,
    ActionGroupComponent,
    ButtonComponent,
    IconComponent,
];
class AreaHeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderModule, imports: [AreaHeaderComponent,
            AreaHeaderContentDirective,
            AreaHeaderSubtitleDirective,
            AvatarComponent,
            ActionGroupComponent,
            ButtonComponent,
            IconComponent], exports: [CoreModule, AreaHeaderComponent,
            AreaHeaderContentDirective,
            AreaHeaderSubtitleDirective,
            AvatarComponent,
            ActionGroupComponent,
            ButtonComponent,
            IconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderModule, imports: [AreaHeaderComponent,
            AvatarComponent,
            ActionGroupComponent,
            ButtonComponent,
            IconComponent, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AreaHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AreaHeaderComponent, AreaHeaderContentDirective, AreaHeaderModule, AreaHeaderSize, AreaHeaderSubtitleDirective };
